/**
 * Terminology Server token vending — used by RTK Query baseQuery.
 * Token is fetched from our backend (/api/terminology/token) and cached.
 */

let cachedToken: string | null = null;
let tokenExpiry = 0;
let tokenPromise: Promise<string | null> | null = null;

async function fetchInternalToken(): Promise<{ token: string | null; expiresInSeconds?: number }> {
    try {
        const response = await fetch('/api/terminology/token');
        if (!response.ok) return { token: null };
        const data = await response.json();
        const token = data.access_token || null;
        const expiresInSeconds = typeof data.expires_in === 'number' ? data.expires_in : undefined;
        return { token, expiresInSeconds };
    } catch (e) {
        console.error('[TerminologyAPI] Failed to fetch internal token', e);
        return { token: null };
    }
}

/** Get cached token or fetch new one (used by RTK Query baseQuery). */
export async function getOrFetchTerminologyToken(): Promise<string | null> {
    if (cachedToken && Date.now() < tokenExpiry) return cachedToken;
    if (tokenPromise) return tokenPromise;

    tokenPromise = (async () => {
        try {
            const { token, expiresInSeconds } = await fetchInternalToken();
            if (token) {
                cachedToken = token;
                const bufferSeconds = 60;
                const ms = expiresInSeconds != null ? (expiresInSeconds - bufferSeconds) * 1000 : 25 * 60 * 1000;
                tokenExpiry = Date.now() + ms;
                return token;
            }
            return null;
        } finally {
            tokenPromise = null;
        }
    })();
    return tokenPromise;
}

/** Clear cached token (e.g. after 401). */
export function clearTerminologyToken(): void {
    cachedToken = null;
    tokenExpiry = 0;
}
